import { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import {
  AlertOctagon,
  Bug,
  Copy,
  FileDown,
  Filter,
  Flame,
  ListTree,
  Skull,
  Target,
  Trash2,
  X,
  Zap,
} from 'lucide-react';
import type { EventBucket, EventStore } from '@/lib/logcat/eventStore';
import type { LogEvent, LogEventKind } from '@/lib/logcat/eventDetector';

interface Props {
  open: boolean;
  onClose: () => void;
  store: EventStore;
  onJump: (ev: LogEvent) => void;
  onCopySummary: (ev: LogEvent) => void;
  onCopyStack: (ev: LogEvent) => void;
  onFilterProcess: (ev: LogEvent) => void;
  onSaveContext: (ev: LogEvent) => void;
  /** Trigger when the drawer-open transitions to true so the store can mark read. */
}

const KIND_META: Record<
  LogEventKind,
  { label: string; tone: string; chip: string; Icon: typeof AlertOctagon }
> = {
  java_crash: { label: 'Java FATAL', tone: 'text-rose-500', chip: 'bg-rose-500/15 text-rose-500', Icon: Bug },
  anr:        { label: 'ANR',        tone: 'text-amber-500', chip: 'bg-amber-500/15 text-amber-500', Icon: Zap },
  native_crash: { label: 'Native',   tone: 'text-fuchsia-500', chip: 'bg-fuchsia-500/15 text-fuchsia-500', Icon: Skull },
  lmk:        { label: 'LMK',         tone: 'text-slate-500', chip: 'bg-slate-500/15 text-slate-500', Icon: ListTree },
  oom:        { label: 'OOM',         tone: 'text-rose-500', chip: 'bg-rose-500/15 text-rose-500', Icon: Flame },
};

export function EventDrawer({
  open,
  onClose,
  store,
  onJump,
  onCopySummary,
  onCopyStack,
  onFilterProcess,
  onSaveContext,
}: Props) {
  const [, setTick] = useState(0);
  useEffect(() => store.subscribe(() => setTick((t) => t + 1)), [store]);

  // Mark read once when the drawer opens.
  useEffect(() => {
    if (open) store.markRead();
  }, [open, store]);

  const { buckets } = store.snapshot();

  if (!open) return null;
  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-black/30"
        onClick={onClose}
        aria-hidden
      />
      <aside
        className="fixed right-0 top-0 z-50 flex h-full w-[480px] flex-col border-l border-line bg-bg-card shadow-2xl"
        role="dialog"
        aria-label="事件中心"
      >
        <header className="flex items-center justify-between border-b border-line px-3 py-2">
          <div className="flex items-center gap-2">
            <AlertOctagon className="h-4 w-4 text-rose-500" />
            <h2 className="text-sm font-semibold">事件中心</h2>
            <span className="badge">共 {buckets.length}</span>
          </div>
          <div className="flex items-center gap-1">
            <button
              className="btn-ghost"
              onClick={() => store.clearAll()}
              title="清空"
              disabled={buckets.length === 0}
            >
              <Trash2 className="h-3.5 w-3.5" />
              <span className="text-xs">清空</span>
            </button>
            <button className="btn-ghost" onClick={onClose} title="关闭 (Esc)">
              <X className="h-4 w-4" />
            </button>
          </div>
        </header>
        <div className="flex-1 overflow-auto p-2 space-y-2">
          {buckets.length === 0 && (
            <div className="px-3 py-6 text-center text-xs text-ink-muted">
              尚未检测到崩溃 / ANR / OOM / 内存被杀事件。
              <br />
              触发一次 <code className="font-mono">am crash &lt;pkg&gt;</code> 试试。
            </div>
          )}
          {buckets.map((b) => (
            <EventCard
              key={b.rep.id}
              bucket={b}
              onJump={() => onJump(b.rep)}
              onCopySummary={() => onCopySummary(b.rep)}
              onCopyStack={() => onCopyStack(b.rep)}
              onFilterProcess={() => onFilterProcess(b.rep)}
              onSaveContext={() => onSaveContext(b.rep)}
            />
          ))}
        </div>
      </aside>
    </>
  );
}

interface CardProps {
  bucket: EventBucket;
  onJump: () => void;
  onCopySummary: () => void;
  onCopyStack: () => void;
  onFilterProcess: () => void;
  onSaveContext: () => void;
}

function EventCard({
  bucket,
  onJump,
  onCopySummary,
  onCopyStack,
  onFilterProcess,
  onSaveContext,
}: CardProps) {
  const { rep, count, lastTs } = bucket;
  const meta = KIND_META[rep.kind];
  const [expand, setExpand] = useState(false);
  const headLines = useMemo(() => rep.snippet.split('\n').slice(0, 4).join('\n'), [rep.snippet]);
  return (
    <div className="rounded-md border border-line bg-bg-soft/40 p-2">
      <div className="flex flex-wrap items-center gap-2 text-[11px]">
        <span className={clsx('inline-flex items-center gap-1 rounded px-1.5 py-0.5 font-medium', meta.chip)}>
          <meta.Icon className="h-3 w-3" />
          {meta.label}
        </span>
        <span className="font-mono text-ink-muted">{shortTs(lastTs)}</span>
        <span className="truncate text-ink">{rep.processName ?? `pid ${rep.pid ?? '?'}`}</span>
        {count > 1 && (
          <span className="badge border-amber-500 text-amber-500">× {count}</span>
        )}
      </div>
      <p className={clsx('mt-1 break-words text-xs font-medium', meta.tone)}>
        {rep.exceptionType ?? rep.signal ?? rep.summary}
      </p>
      {rep.firstFrame && (
        <p className="mt-0.5 truncate font-mono text-[11px] text-ink-muted" title={rep.firstFrame}>
          {rep.firstFrame}
        </p>
      )}
      <pre
        className={clsx(
          'mt-1.5 overflow-auto whitespace-pre-wrap break-all rounded bg-bg-card p-1.5 font-mono text-[10px] leading-snug text-ink-muted',
          expand ? 'max-h-64' : 'max-h-16'
        )}
      >
        {expand ? rep.snippet : headLines}
      </pre>
      <div className="mt-1.5 flex flex-wrap gap-1">
        <button className="btn-ghost text-[11px]" onClick={onJump} title="滚动到事件起点">
          <Target className="h-3 w-3" /> 跳到此处
        </button>
        <button className="btn-ghost text-[11px]" onClick={onCopySummary} title="复制一行摘要">
          <Copy className="h-3 w-3" /> 摘要
        </button>
        <button className="btn-ghost text-[11px]" onClick={onCopyStack} title="复制完整堆栈/上下文">
          <Copy className="h-3 w-3" /> 堆栈
        </button>
        {rep.pid && (
          <button className="btn-ghost text-[11px]" onClick={onFilterProcess} title="把当前过滤设为该 PID">
            <Filter className="h-3 w-3" /> 只看进程
          </button>
        )}
        <button className="btn-ghost text-[11px]" onClick={onSaveContext} title="另存上下文为独立日志文件">
          <FileDown className="h-3 w-3" /> 保存上下文
        </button>
        <button
          className="btn-ghost ml-auto text-[11px]"
          onClick={() => setExpand((v) => !v)}
        >
          {expand ? '折叠' : '展开'}
        </button>
      </div>
    </div>
  );
}

function shortTs(ts: string): string {
  const m = /\s(\d{2}:\d{2}:\d{2})/.exec(ts);
  return m ? m[1] : ts;
}
